"use client";
import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { message } from "antd";
import axios from "axios";
import { useRouter } from "next/navigation";
import "./SelfPickup.scss";
import SelfCreateProductModal from "./modules/SelfCreateProductModal";
import SelfProductDetailsModal from "./modules/SelfProductDetail.Modal";

const SelfPickup = () => {
  const router = useRouter();
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingProductDetail, setLoadingProductDetail] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [vendorInputValue, setVendorInputValue] = useState("");
  const [productInputValue, setProductInputValue] = useState("");
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showProductDetailsModal, setShowProductDetailsModal] = useState(false);
  const [selectedInterestRate, setSelectedInterestRate] = useState(null);

  const [bnplData, setBnplData] = useState({
    is_bnpl: false,
    installment_plan: null,
  });

  const storeId = 23;

  useEffect(() => {
    fetchVendors("");
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (vendorInputValue !== undefined) {
        fetchVendors(vendorInputValue);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [vendorInputValue]);

  useEffect(() => {
    if (
      selectedVendor?.id &&
      selectedCategory &&
      productInputValue !== undefined &&
      !showProductDetailsModal
    ) {
      const delayedProductSearch = setTimeout(() => {
        fetchProducts(selectedVendor.id, selectedCategory, productInputValue);
      }, 300);

      return () => clearTimeout(delayedProductSearch);
    }
  }, [
    productInputValue,
    selectedVendor,
    selectedCategory,
    showProductDetailsModal,
  ]);

  const fetchVendors = async (searchTerm = "") => {
    try {
      setLoadingVendors(true);
      let url = `${process.env.NEXT_PUBLIC_BACKEND}/storefront/b2b_vendor_list?storefront=${storeId}&page=1&limit=100`;

      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await axios.get(url);
      let vendorList =
        response.data?.results || response.data?.data || response.data || [];

      if (searchTerm && searchTerm.trim()) {
        vendorList = vendorList.filter(
          (vendor) =>
            vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vendor.title?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setVendors(vendorList);
    } catch (error) {
      console.error("Failed to fetch vendors:", error);
      message.error("Failed to load vendors");
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchProducts = async (
    vendorId,
    categoryObj = null,
    searchTerm = ""
  ) => {
    try {
      setLoadingProducts(true);

      if (!searchTerm || searchTerm.trim() === "") {
        setProducts([]);
        setSelectedProduct(null);
        setSelectedVariant(null);
        setProductDetail(null);
        setQuantity(1);
        setBnplData({ is_bnpl: false, installment_plan: null });
      }

      let url = `${process.env.NEXT_PUBLIC_BACKEND}/storefront/b2b_products?limit=50&storefront=${storeId}&vendor_id=${vendorId}`;

      if (categoryObj && categoryObj.id) {
        url += `&category_id=${categoryObj.id}`;
      }

      url += `&search=${encodeURIComponent(searchTerm || "")}`;

      const response = await axios.get(url);
      const productList =
        response.data?.data || response.data?.results || response.data || [];
      setProducts(productList);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      message.error("Failed to load products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchProductDetail = async (productHandle) => {
    try {
      setLoadingProductDetail(true);
      setProductDetail(null);

      const response = await axios.get(
        `${process.env.NEXT_PUBLIC_BACKEND}/storefront/product/${productHandle}?storefront=${storeId}&item_type=${selectedCategory.name}&source=retail_form`
      );
      setProductDetail(response.data);
      setShowProductDetailsModal(true);
    } catch (error) {
      console.error("Failed to fetch product detail:", error);
      message.error("Failed to load product details");
    } finally {
      setLoadingProductDetail(false);
    }
  };

  const handleVendorChange = (event, newValue) => {
    setSelectedVendor(newValue);
    setSelectedCategory(null);
    setProducts([]);
    setSelectedProduct(null);
    setSelectedVariant(null);
    setProductDetail(null);
    setProductInputValue("");
    setBnplData({ is_bnpl: false, installment_plan: null });
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setProductInputValue("");

    if (selectedVendor?.id && newValue) {
      fetchProducts(selectedVendor.id, newValue, "");
    } else {
      setProducts([]);
      setSelectedProduct(null);
      setSelectedVariant(null);
      setProductDetail(null);
      setBnplData({ is_bnpl: false, installment_plan: null });
    }
  };

  const handleProductChange = (event, newValue) => {
    if (newValue?.isCreateOption) {
      setShowCreateProductModal(true);
      return;
    }

    if (newValue?.handle) {
      fetchProductDetail(newValue.handle);
    } else {
      setSelectedProduct(null);
      setSelectedVariant(null);
      setProductDetail(null);
      setQuantity(1);
      setBnplData({ is_bnpl: false, installment_plan: null });
    }
  };

  const handleProductSelect = (product, variant, bnplInfo = null) => {
    setSelectedProduct(product);
    setSelectedVariant(variant);
    setProductDetail(product);
    setQuantity(1);

    if (bnplInfo) {
      setBnplData({
        is_bnpl: bnplInfo.is_bnpl || false,
        installment_plan: bnplInfo.installment_plan || null,
      });
    } else {
      setBnplData({ is_bnpl: false, installment_plan: null });
    }

    setShowProductDetailsModal(false);
  };

  const handleCloseProductDetailsModal = () => {
    setShowProductDetailsModal(false);
  };

  const handleQuantityChange = (type) => {
    if (type === "increase") {
      const maxQuantity = selectedVariant?.inventory_quantity || 999;
      if (quantity < maxQuantity) {
        setQuantity((prev) => prev + 1);
      }
    } else if (type === "decrease" && quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const addToCart = async () => {
    if (!selectedProduct || !selectedVariant) {
      message.error("Please select a product first");
      return;
    }

    const card_type = bnplData.is_bnpl ? "bafl_plans" : "all_bank_plans";

    try {
      setLoading(true);

      const body = {
        storefront: storeId,
        line_item: {
          add_ons: [],
          quantity: quantity,
          item_type: selectedVendor?.name || selectedVendor?.title || null,
          variant_price_inventory: selectedVariant.variant_detail?.variant_id,
          is_bnpl: bnplData.is_bnpl,
          card_type: card_type,
          ...(bnplData.installment_plan !== null && {
            installment_plan: bnplData.installment_plan,
          }),
          interest_rate: selectedInterestRate ?? null,
        },
        ip_country: "PK",
        user_agent: navigator.userAgent,
      };

      const headers = {
        "Content-Type": "application/json",
      };

      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_BACKEND}/order/add_item_in_cart`,
        body,
        { headers }
      );

      if (response.data) {
        message.success("Product added to cart successfully!");

        if (response.data.cartid) {
          localStorage.setItem("cartId", response.data.cartid);
        }

        const cartId = response.data.cartid || localStorage.getItem("cartId");
        router.push(`/cart/checkout?id=${cartId}`);
      }
    } catch (error) {
      console.error("Failed to add product to cart:", error);
      const errorMessage =
        error.response?.data?.detail || "Failed to add product to cart";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getProductPrice = () => {
    if (!selectedVariant?.variant_detail) return "N/A";
    const variantDetail = selectedVariant.variant_detail;
    return (
      variantDetail.discounted_price || variantDetail.original_price || "N/A"
    );
  };

  const isAddToCartDisabled = () => {
    return !selectedProduct || !selectedVariant || loading;
  };

  const productOptions = () => {
    const createOption = {
      id: "create-new",
      title: "Create Product",
      isCreateOption: true,
    };
    return products.length > 0 ? [createOption, ...products] : [createOption];
  };

  const handleCreateProductModalClose = () => {
    setShowCreateProductModal(false);
  };

  const handleCreateProductSuccess = (newProduct) => {
    setShowCreateProductModal(false);
    if (selectedVendor?.id && selectedCategory) {
      fetchProducts(selectedVendor.id, selectedCategory, "");
    }
    message.success("Product created successfully!");
  };

  const getInstallmentPlanDisplay = () => {
    if (!bnplData.is_bnpl || !bnplData.installment_plan) return null;

    if (
      typeof bnplData.installment_plan === "string" &&
      !bnplData.installment_plan.startsWith("{")
    ) {
      return bnplData.installment_plan;
    }

    try {
      const plan =
        typeof bnplData.installment_plan === "string"
          ? JSON.parse(bnplData.installment_plan)
          : bnplData.installment_plan;

      return (
        plan.original_text ||
        plan.title ||
        plan.name ||
        `${plan.installments || plan.duration} Installments`
      );
    } catch (error) {
      console.error("Error parsing installment plan:", error);
      return "Selected Installment Plan";
    }
  };

  const getPaymentMethodDisplay = () => {
    if (bnplData.is_bnpl) {
      return {
        label: "BNPL",
        color: "success",
        description: getInstallmentPlanDisplay(),
      };
    } else {
      return {
        label: "Full Payment",
        color: "primary",
        description: "One-time payment",
      };
    }
  };

  const handleVendorInputChange = (event, newInputValue) => {
    setVendorInputValue(newInputValue);
  };

  const handleProductInputChange = (event, newInputValue) => {
    setProductInputValue(newInputValue);
  };

  return (
    <div className="retail-outlet-form">
      <Card className="form-card">
        <CardContent>
          <Typography variant="h4" component="h1" className="form-heading">
            Retail Outlet Form
          </Typography>

          <Box className="form-section">
            <Typography variant="h6" className="field-label">
              Select Seller
            </Typography>
            <Autocomplete
              options={vendors}
              getOptionLabel={(option) => option.name || option.title || ""}
              value={selectedVendor}
              onChange={handleVendorChange}
              onInputChange={handleVendorInputChange}
              inputValue={vendorInputValue}
              loading={loadingVendors}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and choose a seller..."
                  variant="outlined"
                  className="autocomplete-field"
                />
              )}
              className="vendor-autocomplete"
              noOptionsText={
                vendorInputValue ? "No sellers found" : "Type to search sellers"
              }
            />
          </Box>

          <Box className="form-section">
            <Typography variant="h6" className="field-label">
              Select Category
            </Typography>
            <Autocomplete
              options={selectedVendor?.categories || []}
              getOptionLabel={(option) => option.name || ""}
              value={selectedCategory}
              onChange={handleCategoryChange}
              disabled={!selectedVendor}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedVendor
                      ? "Choose a category..."
                      : "Select a seller first"
                  }
                  variant="outlined"
                  className="autocomplete-field"
                />
              )}
              className="category-autocomplete"
              noOptionsText="No categories available"
            />
          </Box>

          <Box className="form-section">
            <Typography variant="h6" className="field-label">
              Select Products
            </Typography>
            <Autocomplete
              options={productOptions()}
              getOptionLabel={(option) => option.title || ""}
              value={selectedProduct}
              onChange={handleProductChange}
              onInputChange={handleProductInputChange}
              inputValue={productInputValue}
              loading={loadingProducts}
              disabled={!selectedVendor || !selectedCategory}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    !selectedVendor
                      ? "Select a seller first"
                      : !selectedCategory
                      ? "Select a category first"
                      : "Search and choose a product..."
                  }
                  variant="outlined"
                  className="autocomplete-field"
                />
              )}
              className="product-autocomplete"
              noOptionsText={
                productInputValue
                  ? "No products found"
                  : "Type to search products"
              }
            />
          </Box>

          <Box className="form-section">
            <Typography variant="h6" className="field-label">
              Price
            </Typography>
            <TextField
              value={getProductPrice()}
              variant="outlined"
              disabled
              className="price-field"
              placeholder="Product price will appear here"
            />
          </Box>

          <Box className="form-section">
            <Box className="quantity-section">
              <Typography variant="body1" className="quantity-label">
                Select Quantity:
              </Typography>
              <Box className="quantity-controls">
                <IconButton
                  onClick={() => handleQuantityChange("decrease")}
                  disabled={quantity <= 1 || !selectedVariant}
                  className="quantity-btn"
                >
                  <Remove />
                </IconButton>
                <TextField
                  value={quantity}
                  variant="outlined"
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value > 0) {
                      setQuantity(value);
                    }
                  }}
                  className="quantity-input"
                  inputProps={{ style: { textAlign: "center" } }}
                />
                <IconButton
                  onClick={() => handleQuantityChange("increase")}
                  disabled={!selectedVariant}
                  className="quantity-btn"
                >
                  <Add />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box className="form-section">
            <Button
              variant="contained"
              size="large"
              onClick={addToCart}
              disabled={isAddToCartDisabled()}
              loading={loading}
              className="checkout-btn"
            >
              {loading ? "Adding to Cart..." : "Proceed to Checkout"}
            </Button>
          </Box>

          {selectedProduct && selectedVariant && (
            <Box className="product-summary">
              <Typography variant="h6">Product Summary</Typography>
              <Typography variant="body2">
                <strong>Product:</strong> {selectedProduct.title}
              </Typography>
              <Typography variant="body2">
                <strong>Variant:</strong> {selectedVariant.title}
                {selectedVariant.option1 && ` - ${selectedVariant.option1}`}
                {selectedVariant.option2 && ` - ${selectedVariant.option2}`}
                {selectedVariant.option3 && ` - ${selectedVariant.option3}`}
              </Typography>
              <Typography variant="body2">
                <strong>Seller:</strong>{" "}
                {selectedVendor?.name || selectedVendor?.title}
              </Typography>
              {selectedCategory && (
                <Typography variant="body2">
                  <strong>Category:</strong> {selectedCategory.name}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Price:</strong> Rs. {getProductPrice()}
              </Typography>
              <Typography variant="body2">
                <strong>Quantity:</strong> {quantity}
              </Typography>
              <Typography variant="body2">
                <strong>Total:</strong> Rs.{" "}
                {(
                  parseFloat(getProductPrice().replace(/,/g, "")) * quantity
                ).toLocaleString()}
              </Typography>

              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <strong>Payment Method:</strong>
                  <Chip
                    label={getPaymentMethodDisplay().label}
                    color={getPaymentMethodDisplay().color}
                    size="small"
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Details:</strong>{" "}
                  {getPaymentMethodDisplay().description}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <SelfProductDetailsModal
        open={showProductDetailsModal}
        onClose={handleCloseProductDetailsModal}
        productDetail={productDetail}
        loading={loadingProductDetail}
        onSelectProduct={handleProductSelect}
        selectedInterestRate={selectedInterestRate}
        setSelectedInterestRate={setSelectedInterestRate}
      />

      <SelfCreateProductModal
        open={showCreateProductModal}
        onClose={handleCreateProductModalClose}
        onSuccess={handleCreateProductSuccess}
        selectedVendor={selectedVendor}
        selectedCategory={selectedCategory}
        storeId={storeId}
      />
    </div>
  );
};

export default SelfPickup;
